import {
  CalendarDays,
  ChevronRight,
  FileSignature,
  Fingerprint,
  Plane,
  WalletCards,
} from "lucide-react";

export type RrhhSection =
  | "fichaje"
  | "vacaciones"
  | "agenda"
  | "nominas"
  | "documentacion";

type Props = {
  active: RrhhSection;
  onChange: (section: RrhhSection) => void;
};

const sections = [
  {
    id: 'fichaje',
    title: 'Fichaje',
    subtitle: 'Entrada/salida y contador',
    icon: Fingerprint,
  },
  {
    id: 'vacaciones',
    title: 'Vacaciones',
    subtitle: 'Solicitudes y saldo',
    icon: Plane,
  },
  {
    id: 'agenda',
    title: 'Agenda',
    subtitle: 'Calendario y eventos personales',
    icon: CalendarDays,
  },
  {
    id: 'nominas',
    title: 'Nóminas',
    subtitle: 'Descargas mensuales',
    icon: WalletCards,
  },
  {
    id: 'documentacion',
    title: 'Documentación',
    subtitle: 'Contratos y anexos',
    icon: FileSignature,
  },
] satisfies Array<{
  id: RrhhSection;
  title: string;
  subtitle: string;
  icon: typeof Fingerprint;
}>;

export function RrhhSectionNav({ active, onChange }: Props) {
  return (
    <aside className="dashboard-component-shell h-full min-h-full overflow-hidden rounded-[34px] p-4">
      <div className="mb-4 flex items-center justify-between rounded-[24px] border border-emerald-100 bg-emerald-50/70 px-4 py-3">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
            Recursos Humanos
          </div>

          <div className="mt-1 text-sm font-black text-slate-950">
            Módulos internos
          </div>
        </div>

        <span className="rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-[11px] font-black text-emerald-700 shadow-[0_8px_18px_rgba(16,185,129,.08)]">
          {sections.length}
        </span>
      </div>

      <div className="space-y-2.5">
        {sections.map((section, index) => {
          const Icon = section.icon;
          const isActive = active === section.id;

          return (
            <button
              key={section.id}
              type="button"
              onClick={() => onChange(section.id)}
              className={[
                "group relative flex w-full items-center gap-3 overflow-hidden rounded-[26px] border p-3.5 text-left transition-all duration-300",
                isActive
                  ? "border-emerald-200 bg-[linear-gradient(135deg,#ecfdf5,#f0fdfa)] text-emerald-800 shadow-[0_18px_44px_rgba(16,185,129,.14)]"
                  : "border-[#e8eef0] bg-white text-slate-600 shadow-[0_8px_22px_rgba(15,23,42,.045)] hover:-translate-y-1 hover:border-emerald-200 hover:bg-emerald-50/80 hover:text-emerald-700 hover:shadow-[0_18px_40px_rgba(16,185,129,.11)]",
              ].join(" ")}
              style={{ animationDelay: `${index * 55}ms` }}
            >
              {isActive && (
                <>
                  <span className="absolute inset-y-3 left-0 w-1 rounded-r-full bg-gradient-to-b from-[#00d09c] to-[#00c3af]" />
                  <span className="absolute -right-16 top-[-60px] h-36 w-36 rounded-full bg-emerald-300/28 blur-3xl" />
                </>
              )}

              <span
                className={[
                  "grid h-12 w-12 shrink-0 place-items-center rounded-2xl border shadow-[0_12px_26px_rgba(15,23,42,.065)] backdrop-blur-xl transition-all duration-300 group-hover:-rotate-3 group-hover:scale-110",
                  isActive
                    ? "border-emerald-100 bg-white text-emerald-700"
                    : "border-slate-200 bg-white text-slate-500 group-hover:border-emerald-100 group-hover:text-emerald-700",
                ].join(" ")}
              >
                <Icon size={20} />
              </span>

              <span className="min-w-0 flex-1">
                <span className="block text-base font-black tracking-tight text-slate-950">
                  {section.title}
                </span>

                <span className="mt-0.5 block text-xs font-semibold leading-5 text-slate-500">
                  {section.subtitle}
                </span>
              </span>

              <ChevronRight
                size={18}
                className={[
                  "transition-all duration-300",
                  isActive
                    ? "translate-x-1 text-emerald-500"
                    : "text-slate-300 group-hover:translate-x-1 group-hover:text-emerald-500",
                ].join(" ")}
              />
            </button>
          );
        })}
      </div>
    </aside>
  );
}